/**
 * GoHighLevel (LeadConnector) — TypeBox schemas.
 *
 * Story 2.6 — defines wire shapes consumed by the API's `GHLService`
 * (`apps/api/src/services/ghl/ghl.service.ts`) and by the GHL webhook routes
 * landing in Stories 2.7 / 2.8. Property names mirror the GoHighLevel
 * documented payloads (camelCase) — the API layer is the only place that maps
 * snake_case ↔ camelCase, and GHL already exposes camelCase, so no rename is
 * needed here.
 *
 * Constitution Article IV (No Invention): every field below is either
 *   1. documented by GoHighLevel (services.leadconnectorhq.com), or
 *   2. explicitly listed in `epic-E2-external-integrations.md` §2.C.
 *
 * `additionalProperties: true` is intentional on the response shapes. GHL
 * regularly adds new fields server-side; we only validate the ones we read so
 * a release on their side never breaks our parser. The webhook payload is
 * stricter (we reject unknown shapes via `dedupe_key` selection downstream).
 */
import { Type } from '@sinclair/typebox';
// ============================================================================
// Contact (GET /contacts/{id})
// ============================================================================
/**
 * GHL contact — minimal projection consumed by the agent (phone, name,
 * email, customFields). The full GHL response carries dozens of fields; we
 * keep `additionalProperties: true` so we never crash on a new column.
 *
 * Phone is optional because GHL allows email-only contacts; the caller is
 * responsible for handling missing-phone (typically: skip + audit).
 */
export const GHLContactCustomField = Type.Object({
    id: Type.String({ minLength: 1 }),
    key: Type.Optional(Type.String()),
    value: Type.Optional(Type.Unknown()),
}, { additionalProperties: true });
export const GHLContact = Type.Object({
    id: Type.String({ minLength: 1 }),
    locationId: Type.Optional(Type.String()),
    firstName: Type.Optional(Type.String()),
    lastName: Type.Optional(Type.String()),
    name: Type.Optional(Type.String()),
    email: Type.Optional(Type.String()),
    phone: Type.Optional(Type.String()),
    /** ISO-8601 — GHL emits e.g. "2024-08-13T12:34:56.789Z". */
    dateAdded: Type.Optional(Type.String()),
    dateUpdated: Type.Optional(Type.String()),
    tags: Type.Optional(Type.Array(Type.String())),
    customFields: Type.Optional(Type.Array(GHLContactCustomField)),
    source: Type.Optional(Type.String()),
}, {
    additionalProperties: true,
    description: 'GoHighLevel Contact (Contacts API) — partial projection',
});
// ============================================================================
// Pipeline lead (GET /opportunities/search?pipelineId=&pipelineStageId=)
// ============================================================================
/**
 * GHL opportunity custom field — DIFFERENT shape from `GHLContactCustomField`
 * (`{id, key, value}`). Confirmed live (2026-07-28, Story 10.5 investigation
 * via `GET /opportunities/search`): each entry carries `id` + `type` + ONE
 * of `fieldValueString`/`fieldValueNumber`/`fieldValueDate` depending on the
 * field's data type (`SINGLE_OPTIONS`/`TEXT` → string, `MONETORY`/`NUMBER` →
 * number, `DATE` → epoch ms number under `fieldValueDate`).
 */
export const GHLOpportunityCustomField = Type.Object({
    id: Type.String({ minLength: 1 }),
    type: Type.Optional(Type.String()),
    fieldValueString: Type.Optional(Type.String()),
    fieldValueNumber: Type.Optional(Type.Number()),
    fieldValueDate: Type.Optional(Type.Number()),
}, { additionalProperties: true });
/**
 * GHL opportunity / pipeline-lead row. Used by `listLeadsByStage` to feed
 * the cron worker (Story 2.8). `contactId` is the link back to the contact
 * record — that's how we resolve a phone for outbound messaging.
 *
 * `customFields` added Story 10.5 (2026-07-28) — needed to read the
 * "Produtos" custom field off an opportunity in the "Venda realizada" stage.
 */
export const GHLPipelineLead = Type.Object({
    id: Type.String({ minLength: 1 }),
    name: Type.Optional(Type.String()),
    pipelineId: Type.String({ minLength: 1 }),
    pipelineStageId: Type.String({ minLength: 1 }),
    contactId: Type.String({ minLength: 1 }),
    locationId: Type.Optional(Type.String()),
    status: Type.Optional(Type.String()),
    monetaryValue: Type.Optional(Type.Number()),
    /** GHL User ID of the salesperson this opportunity is assigned to. */
    assignedTo: Type.Optional(Type.String()),
    customFields: Type.Optional(Type.Array(GHLOpportunityCustomField)),
    /** ISO-8601 timestamps. */
    createdAt: Type.Optional(Type.String()),
    updatedAt: Type.Optional(Type.String()),
}, {
    additionalProperties: true,
    description: 'GoHighLevel Opportunity / Pipeline lead — partial projection',
});
// ============================================================================
// Webhook payload — outbound webhook from GHL on lead-new
// ============================================================================
/**
 * Outbound webhook GHL fires when a lead enters the configured stage. The
 * exact shape is configurable per workflow trigger in GHL — we accept any
 * combination of `id`/`contactId` (some workflows wrap, some flat) and let
 * the route normalize. `eventType` is documented by GHL.
 */
export const GHLLeadNewWebhookPayload = Type.Object({
    /** Unique idempotency key; we map this to `webhooks_log.dedupe_key`. */
    id: Type.Optional(Type.String()),
    type: Type.Optional(Type.String()),
    eventType: Type.Optional(Type.String()),
    locationId: Type.Optional(Type.String()),
    /** `pipelineId`/`pipelineStageId` come either at root or under `data`. */
    pipelineId: Type.Optional(Type.String()),
    pipelineStageId: Type.Optional(Type.String()),
    contactId: Type.Optional(Type.String()),
    opportunityId: Type.Optional(Type.String()),
    timestamp: Type.Optional(Type.String()),
    contact: Type.Optional(Type.Object({
        id: Type.Optional(Type.String()),
        phone: Type.Optional(Type.String()),
        email: Type.Optional(Type.String()),
        firstName: Type.Optional(Type.String()),
        lastName: Type.Optional(Type.String()),
    }, { additionalProperties: true })),
    /** Raw envelope GHL sometimes wraps everything inside. */
    data: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
}, {
    additionalProperties: true,
    description: 'GoHighLevel outbound webhook — lead entered configured stage',
});
//# sourceMappingURL=ghl.js.map